/**
 * Utility to format errors cleanly and human-readably for users,
 * avoiding raw SQL constraints or cryptic developer messages.
 */
export function formatUserFriendlyError(err: unknown, defaultFallback = 'An unexpected error occurred during the operation. Please review your inputs and try again.'): string {
  if (!err) return defaultFallback;

  const raw = typeof err === 'string' ? err : (err as any).message || String(err);

  // SQL / PostgreSQL Not Null constraints
  if (raw.includes('violates not-null constraint') || raw.includes('null value in column')) {
    const match = raw.match(/column "([^"]+)"/);
    const colName = match ? match[1] : '';
    const fieldMap: Record<string, string> = {
      numerator_value: 'Numerator (Event Count)',
      denominator_value: 'Denominator (Flight Hours / Landings)',
      period: 'Period (YYYY-MM)',
      indicator_id: 'Safety Performance Indicator (SPI)',
      title: 'Title',
      name: 'Name',
      description: 'Description',
      department: 'Department',
      organization_id: 'Organization',
      created_by: 'Created By',
    };
    const humanField = fieldMap[colName] || (colName ? `field "${colName}"` : 'Required fields');
    return `Please complete all required fields: ${humanField} cannot be empty.`;
  }

  // Unique constraint
  if (raw.includes('unique constraint') || raw.includes('duplicate key') || raw.includes('ON CONFLICT')) {
    return 'A record for this period or code already exists in the system.';
  }

  // Foreign key constraint
  if (raw.includes('foreign key constraint') || raw.includes('violates foreign key')) {
    return 'The referenced record was not found or may have been removed.';
  }

  // Division by zero or math errors
  if (raw.includes('division by zero')) {
    return 'Denominator cannot be zero. Please enter a valid flight hour or sector count.';
  }

  // Network / Connection
  if (raw.includes('Failed to fetch') || raw.includes('NetworkError') || raw.includes('ECONNREFUSED')) {
    return 'Could not establish connection to the server. Please check your network or server status.';
  }

  // HTML / JSON parse error
  if (raw.includes("Unexpected token '<'") || raw.includes('<!DOCTYPE')) {
    return 'Server endpoint returned an invalid response. Please verify the server is running and route is available.';
  }
  if (raw.includes('Unexpected token') || raw.includes('is not valid JSON')) {
    return 'The server returned an unparseable data format. Data integrity has been preserved.';
  }

  // HTTP status
  if (raw.includes('HTTP error! status: 404') || raw.includes('HTTP 404')) {
    return 'The requested record or API route was not found on the server.';
  }
  if (raw.includes('HTTP error! status: 403') || raw.includes('Forbidden')) {
    return 'You do not have permission to perform this action.';
  }
  if (raw.includes('HTTP error! status: 401') || raw.includes('Unauthorized')) {
    return 'Your session has expired. Please log in again.';
  }

  // If already clean message (no SQL tags, no cryptic syntax)
  if (raw.length < 200 && !raw.includes('Error:') && !raw.includes('at ') && !raw.includes('relation "')) {
    return raw;
  }

  return defaultFallback;
}
